import { useState, useEffect } from 'react';

// GPS位置情報取得のカスタムフック
export const useGeolocation = () => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!navigator.geolocation) {
      setError('お使いのブラウザは位置情報をサポートしていません');
      setLoading(false);
      return;
    }

    const getCurrentPosition = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
          setLoading(false);
        },
        (error) => {
          let errorMessage = '位置情報の取得に失敗しました';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = '位置情報へのアクセスが拒否されました';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = '位置情報が利用できません';
              break;
            case error.TIMEOUT:
              errorMessage = '位置情報の取得がタイムアウトしました';
              break;
          }
          
          setError(errorMessage);
          setLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5分間キャッシュ
        }
      );
    };

    getCurrentPosition();
  }, []);

  const refetch = () => {
    setLoading(true);
    setError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setLoading(false);
      },
      (error) => {
        setError('位置情報の再取得に失敗しました');
        setLoading(false);
      }
    );
  };

  return { location, error, loading, refetch };
};

// 距離計算ユーティリティ（ハーバーサイン公式）
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // 地球の半径（km）
  const dLat = toRadian(lat2 - lat1);
  const dLng = toRadian(lng2 - lng1);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadian(lat1)) * Math.cos(toRadian(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // 距離（km）
};

const toRadian = (degree) => {
  return degree * (Math.PI / 180);
};

// 都道府県データ
export const PREFECTURES = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
  '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
  '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
  '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
];

// 住所から座標を取得する関数（Google Geocoding API使用）
export const geocodeAddress = async (address, apiKey) => {
  if (!apiKey) {
    throw new Error('Google Maps API キーが設定されていません');
  }

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`
    );
    
    const data = await response.json();
    
    if (data.status === 'OK' && data.results.length > 0) {
      const { lat, lng } = data.results[0].geometry.location;
      return { lat, lng };
    } else {
      throw new Error('住所から座標の取得に失敗しました');
    }
  } catch (error) {
    throw new Error(`位置情報の取得に失敗しました: ${error.message}`);
  }
};

// 座標から住所を取得する関数（Google Reverse Geocoding API使用）
export const reverseGeocode = async (lat, lng, apiKey) => {
  if (!apiKey) {
    throw new Error('Google Maps API キーが設定されていません');
  }

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}&language=ja`
    );
    
    const data = await response.json();
    
    if (data.status === 'OK' && data.results.length > 0) {
      const result = data.results[0];
      
      // 都道府県を抽出
      const prefectureComponent = result.address_components.find(
        component => component.types.includes('administrative_area_level_1')
      );
      
      // 市区町村を抽出
      const cityComponent = result.address_components.find(
        component => component.types.includes('locality') || component.types.includes('administrative_area_level_2')
      );
      
      return {
        full_address: result.formatted_address,
        prefecture: prefectureComponent?.long_name || '',
        city: cityComponent?.long_name || '',
        coordinates: { lat, lng }
      };
    } else {
      throw new Error('座標から住所の取得に失敗しました');
    }
  } catch (error) {
    throw new Error(`住所の取得に失敗しました: ${error.message}`);
  }
};

// 求人の距離でソート
export const sortJobsByDistance = (jobs, userLocation) => {
  if (!userLocation) return jobs;
  
  return jobs.map(job => {
    // 求人に座標がある場合は距離を計算
    if (job.latitude && job.longitude) {
      const distance = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        job.latitude,
        job.longitude
      );
      return { ...job, distance };
    }
    return { ...job, distance: null };
  }).sort((a, b) => {
    if (a.distance === null && b.distance === null) return 0;
    if (a.distance === null) return 1;
    if (b.distance === null) return -1;
    return a.distance - b.distance;
  });
};

export default {
  useGeolocation,
  calculateDistance,
  PREFECTURES,
  geocodeAddress,
  reverseGeocode,
  sortJobsByDistance
};